import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  EmptyState,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconBuildings } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  useCreateTenant,
  useTenants,
  type TenantSummary,
} from '../api/tenants';
import { canCreateTenants, useSession } from '../auth/session';
import { OnboardingBadge, StampingBadge } from '../tenants/status';
import { PageHeader } from '../components/PageHeader';
import { ErrorAlert } from '../components/ErrorAlert';
import { TableSkeleton } from '../components/Skeletons';

export const Route = createFileRoute('/tenants')({
  component: TenantsPage,
});

function TenantsPage() {
  const { t } = useTranslation(['tenants', 'common']);
  const { data: session } = useSession();
  const { data: tenants, isPending, isError, error } = useTenants();
  const [createOpen, setCreateOpen] = useState(false);

  const canCreate = canCreateTenants(session);
  const viewingAsPartner = Boolean(session?.viewAsPartner);
  const partnerScoped = viewingAsPartner || (session?.partnerBooks?.length ?? 0) > 0;

  return (
    <Stack gap="md">
      <PageHeader
        title={t('tenants:title')}
        subtitle={
          partnerScoped ? t('tenants:subtitlePartnerView') : t('tenants:subtitle')
        }
        actions={
          <Tooltip label={t('tenants:cannotAddTenant')} disabled={canCreate}>
            <Button disabled={!canCreate} onClick={() => setCreateOpen(true)}>
              {t('tenants:addTenant')}
            </Button>
          </Tooltip>
        }
      />

      {isPending && <TableSkeleton columns={5} />}

      {isError && <ErrorAlert title={t('tenants:loadError')} error={error} />}

      {tenants && tenants.length === 0 && (
        <Card withBorder padding="xl">
          <EmptyState
            icon={<IconBuildings size={28} stroke={1.5} />}
            withIndicatorBackground
            title={partnerScoped ? t('tenants:emptyTitlePartnerView') : t('tenants:emptyTitle')}
            description={
              partnerScoped
                ? t('tenants:emptyDescriptionPartnerView')
                : t('tenants:emptyDescription')
            }
          >
            {canCreate ? (
              <EmptyState.Actions>
                <Button onClick={() => setCreateOpen(true)}>{t('tenants:addTenant')}</Button>
              </EmptyState.Actions>
            ) : null}
          </EmptyState>
        </Card>
      )}

      {tenants && tenants.length > 0 && <TenantTable tenants={tenants} />}

      <CreateTenantModal opened={createOpen} onClose={() => setCreateOpen(false)} />
    </Stack>
  );
}

function TenantTable({ tenants }: { tenants: TenantSummary[] }) {
  const { t } = useTranslation('tenants');
  return (
    <Card withBorder padding={0}>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('colTenant')}</Table.Th>
            <Table.Th>{t('colOnboarding')}</Table.Th>
            <Table.Th>{t('colStamping')}</Table.Th>
            <Table.Th>{t('colDomains')}</Table.Th>
            <Table.Th>{t('colSnapshot')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tenants.map((tenant) => (
            <Table.Tr key={tenant.id}>
              <Table.Td>
                <Link
                  to="/tenants/$tenantId"
                  params={{ tenantId: tenant.id }}
                  search={{ tab: 'settings' }}
                  style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                >
                  <Group gap="sm" wrap="nowrap">
                    <Center
                      w={28}
                      h={28}
                      fz={13}
                      fw={600}
                      c="iris.7"
                      style={{
                        borderRadius: 'var(--mantine-radius-sm)',
                        backgroundColor: 'var(--mantine-color-iris-1)',
                        flexShrink: 0,
                      }}
                    >
                      {tenant.name.slice(0, 1).toUpperCase()}
                    </Center>
                    <Stack gap={0}>
                      <Text fw={500}>{tenant.name}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {tenant.slug}
                      </Text>
                    </Stack>
                  </Group>
                </Link>
              </Table.Td>
              <Table.Td>
                <OnboardingBadge state={tenant.onboardingState} />
              </Table.Td>
              <Table.Td>
                <StampingBadge tenant={tenant} />
              </Table.Td>
              <Table.Td>
                {tenant.verifiedDomains.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    {t('noneSynced')}
                  </Text>
                ) : (
                  <Badge variant="default" color="gray" size="sm">
                    {tenant.verifiedDomains.length}
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed" ff="monospace">
                  v{tenant.snapshotVersion}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function CreateTenantModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation(['tenants', 'common']);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const create = useCreateTenant();
  const navigate = Route.useNavigate();

  // Same shape the onboarding PowerShell validates, so a name that works here works there.
  const slugPattern = /^[a-z0-9][a-z0-9-]{1,62}$/;
  const slugValid = slugPattern.test(slug);

  const submit = () => {
    create.mutate(
      { name: name.trim(), slug },
      {
        onSuccess: (tenant) => {
          setName('');
          setSlug('');
          onClose();
          void navigate({
            to: '/tenants/$tenantId',
            params: { tenantId: tenant.id },
            search: { tab: 'settings' },
          });
        },
      },
    );
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('tenants:addTenant')}>
      <Stack>
        <TextInput
          label={t('tenants:orgName')}
          placeholder={t('tenants:orgNamePlaceholder')}
          value={name}
          onChange={(e) => {
            const next = e.currentTarget.value;
            setName(next);
            // Suggest a slug until the operator edits one themselves.
            if (slug === '' || slug === suggestSlug(name)) {
              setSlug(suggestSlug(next));
            }
          }}
        />
        <TextInput
          label={t('tenants:slug')}
          description={t('tenants:slugDescription')}
          value={slug}
          onChange={(e) => setSlug(e.currentTarget.value)}
          error={slug !== '' && !slugValid ? t('tenants:slugError') : null}
        />

        {create.isError && (
          <Alert color="red" title={t('tenants:createError')}>
            {create.error instanceof Error ? create.error.message : t('common:unknownError')}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={submit}
            loading={create.isPending}
            disabled={name.trim() === '' || !slugValid}
          >
            {t('common:create')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}
